import { useState, useCallback, useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faAward, faChartLine, faChartPie, faWaveSquare } from '@fortawesome/free-solid-svg-icons';

import Descargas from '../../components/Dashboard/Charts/Descargas';
import Estadisticas from '../../components/Dashboard/Estadisticas';
import Tops from '../../components/Dashboard/Tops';
import Proyectos from '../../components/Dashboard/Charts/Proyectos';
import TiposInformacion from '../../components/Dashboard/Charts/TiposInformacion';
import Anios from '../../components/Dashboard/Charts/Anios';
import Especies from '../../components/Dashboard/Charts/Especies';
import Puertos from '../../components/Dashboard/Charts/Puertos';
import Regiones from '../../components/Dashboard/Charts/Regiones';
import Spinner from '../../components/Spinner';

import Sidebav from '../../layouts/Sidenav';

import axios from 'axios';

const DashboardPage = ({ verifyToken }) =>
{
    const [fetch, setFetch] = useState(false);
    const [estadisticas, setEstadisticas] = useState([]);
    const [table, setTable] = useState([]);
    const [chart, setChart] = useState({});
    const [loading, setLoading] = useState(false);

    const appDebug = process.env.REACT_APP_DEBUG;
    const apiUrl = process.env.REACT_APP_API_URL;
    const secretKey = process.env.REACT_APP_SECRET_KEY;

    const division = '';

    const fetchData = useCallback(async () =>
    {
        setFetch(true);

        try
        {
            const responseEstadisticas = await axios.get(`${apiUrl}/v2/pesqueria/estadisticas`);

            const estadisticas = verifyToken(responseEstadisticas, appDebug, secretKey);

            if (estadisticas.length === 0) { return console.log(new Error('Signature is not valid.')); }

            setEstadisticas(estadisticas.data);

            const table = {};

            const responseRanking = await axios.get(`${apiUrl}/v2/pesqueria/ranking?limit=5`);

            const ranking = verifyToken(responseRanking, appDebug, secretKey);

            if (ranking.length === 0) { return console.log(new Error('Signature is not valid.')); }

            table.rankingProyectos = ranking.proyectos;
            table.rankingTiposInformacion = ranking.tipos_informacion;
            table.rankingAnios = ranking.anios;
            table.rankingEspecies = ranking.especies;
            table.rankingPuertos = ranking.puertos;
            table.rankingRegiones = ranking.regiones;

            setTable(table);

            const chart = {};

            const responseQueryParams = await axios.get(`${apiUrl}/v2/pesqueria/ranking?total=1&order=desc&limit=6`);

            const queryParams = verifyToken(responseQueryParams, appDebug, secretKey);

            if (queryParams.length === 0) { return console.log(new Error('Signature is not valid.')); }

            chart.descargas = queryParams.descargas.map(descarga => descarga.FECHA);
            chart.registrosDescargas = queryParams.descargas.map(descarga => descarga.REGISTROS);
            chart.megabytesDescargas = queryParams.descargas.map(descarga => descarga.MEGABYTES);
            chart.proyectos = queryParams.proyectos.map(proyecto => proyecto.NOMBRE);
            chart.numeroArchivosProyectos = queryParams.proyectos.map(proyecto => proyecto.NUM_ARCHIVOS);
            chart.porcentajeProyectos = queryParams.proyectos.map(proyecto => proyecto.PORCENTAJE);
            chart.tiposInformacion = queryParams.tipos_informacion.map(tipoInformacion => tipoInformacion.TIPO_INFORMACION);
            chart.numeroArchivosTiposInformacion = queryParams.tipos_informacion.map(tipoInformacion => tipoInformacion.NUM_ARCHIVOS);
            chart.porcentajeTiposInformacion = queryParams.tipos_informacion.map(tipoInformacion => tipoInformacion.PORCENTAJE);
            chart.anios = queryParams.anios.map(anio => anio.ANIO);
            chart.numeroArchivosAnios = queryParams.anios.map(anio => anio.NUM_ARCHIVOS);
            chart.especies = queryParams.especies.map(especie => especie.NOMBRE);
            chart.numeroArchivosEspecies = queryParams.especies.map(especie => especie.NUM_ARCHIVOS);
            chart.puertos = queryParams.puertos.map(puerto => puerto.NOMBRE);
            chart.numeroArchivosPuertos = queryParams.puertos.map(puerto => puerto.NUM_ARCHIVOS);
            chart.regiones = queryParams.regiones.map(region => region.NOMBRE);
            chart.numeroArchivosRegiones = queryParams.regiones.map(region => region.NUM_ARCHIVOS);

            setChart(chart);
            setLoading(true);
        }
        catch (error)
        {
            console.error(error);
        }
    }, [appDebug, apiUrl, secretKey, verifyToken]);

    useEffect(() =>
    {
        fetch === false && fetchData();
    }, [fetchData, fetch]);

    const Dashboard = () =>
    {
        return(
            <div className="container-fluid px-4 pt-3 pb-4" style={{ backgroundColor: '#f8f9fb' }}>

                {
                    loading ?
                    (
                        <>
                            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
                                <div>
                                    <h1 className="h3 mb-2 fw-bold text-dark">Dashboard General</h1>
                                    <p className="text-muted mb-0">Tendencias e indicadores principales para el área pesquera.</p>
                                </div>
                            </div>

                            {
                                chart.descargas && chart.registrosDescargas &&
                                (
                                    <div className="card border-0 shadow-sm rounded-5 mb-4 overflow-hidden">
                                        <div className="card-header rounded-top-5 bg-white border-bottom d-flex align-items-center gap-2">
                                            <FontAwesomeIcon className="text-primary" icon={faChartLine} />
                                            <span className="fw-semibold">Descargas recientes</span>
                                        </div>
                                        <div className="card-body p-3 p-lg-4">
                                            <Descargas labels={chart.descargas} data={chart.registrosDescargas} megabytes={chart.megabytesDescargas} />
                                        </div>
                                    </div>
                                )
                            }

                            <div className="d-flex align-items-center justify-content-center gap-3 text-secondary my-4">
                                <div className="border-top flex-grow-1"></div>
                                <span className="badge rounded-pill py-2 px-4 fw-semibold text-secondary bg-white border border-secondary shadow-sm" style={{ fontSize: '0.85rem' }}>
                                    Rankings
                                </span>
                                <div className="border-top flex-grow-1"></div>
                            </div>

                            <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-6 justify-content-around g-4 mb-4">

                                {
                                    estadisticas?.map((estadistica, index) =>
                                    (
                                        <Estadisticas
                                            key={index}
                                            col={'col'}
                                            card={'card border-0 shadow-sm rounded-5 overflow-hidden mb-4 h-100'}
                                            header={estadistica.titulo}
                                            body={estadistica.data}
                                            small={estadistica.subtitulo}
                                        />
                                    ))
                                }

                            </div>

                            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4 mb-4">

                                {
                                    table.rankingProyectos &&
                                    (
                                        <Tops top={'Top 5: Proyectos'} item={'Nombre'} ranking={table.rankingProyectos} registros={true} />
                                    )
                                }

                                {
                                    table.rankingTiposInformacion &&
                                    (
                                        <Tops top={'Top 5: Tipos de Información'} item={'Nombre'} ranking={table.rankingTiposInformacion} />
                                    )
                                }

                                {
                                    table.rankingEspecies &&
                                    (
                                        <div className="col">
                                            <div className="card border rounded-5 shadow-sm h-100 bg-white">
                                                <div className="card-header rounded-top-5 bg-white border-bottom py-3 d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon className="text-primary" icon={faAward} />
                                                    <span className="fw-semibold">Top 5: Especies</span>
                                                </div>
                                                <div className="card-body p-0">
                                                    <div className="table-responsive px-3 pb-3">
                                                        <table className="table table-sm table-borderless align-middle mb-0" style={{ fontFamily: 'Inter, "Segoe UI", sans-serif', fontSize: '0.95rem' }}>
                                                            <thead>
                                                                <tr className="text-secondary small">
                                                                    <th scope="col" className="text-start py-2" style={{ fontFamily: 'Inter, "Segoe UI", sans-serif' }}>{'N°'}</th>
                                                                    <th scope="col" className="text-start py-2" style={{ fontFamily: 'Inter, "Segoe UI", sans-serif' }}>{'Nombre'}</th>
                                                                    <th scope="col" className="text-end py-2" style={{ fontFamily: 'Inter, "Segoe UI", sans-serif' }}>{'Núm. Archivos'}</th>
                                                                    <th scope="col" className="text-end py-2" style={{ fontFamily: 'Inter, "Segoe UI", sans-serif' }}>{'%'}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    table.rankingEspecies.map((especie, index) =>
                                                                    (
                                                                        <tr key={index + 1} className="border-bottom">
                                                                            <th scope="row" className="small text-center py-2 text-body">{index + 1}</th>
                                                                            <td className="text-start py-2 text-body">
                                                                                {especie.NOMBRE}
                                                                                {
                                                                                    especie.NOMBRE_CIENTIFICO && especie.NOMBRE_CIENTIFICO !== 'Sin nombre cientifico' &&
                                                                                    (
                                                                                        <span className="text-muted small p-2">{'('}<i>{especie.NOMBRE_CIENTIFICO}</i>{')'}</span>
                                                                                    )
                                                                                }
                                                                            </td>
                                                                            <td className="text-end font-monospace py-2 text-body">{especie.NUM_ARCHIVOS.toLocaleString('es-CL')}</td>
                                                                            <td className="text-end fw-bold py-2 text-primary">{especie.PORCENTAJE}</td>
                                                                        </tr>
                                                                    ))
                                                                }
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    table.rankingRegiones &&
                                    (
                                        <Tops top={'Top 5: Regiones'} item={'Nombre'} ranking={table.rankingRegiones} />
                                    )
                                }

                                {
                                    table.rankingPuertos &&
                                    (
                                        <Tops top={'Top 5: Puertos'} item={'Nombre'} ranking={table.rankingPuertos} />
                                    )
                                }

                                {
                                    table.rankingAnios &&
                                    (
                                        <Tops top={'Top 5: Años'} item={'Año'} ranking={table.rankingAnios} />
                                    )
                                }

                            </div>

                            <div className="d-flex align-items-center justify-content-center gap-3 text-secondary my-4">
                                <div className="border-top flex-grow-1"></div>
                                    <span className="badge rounded-pill py-2 px-4 fw-semibold text-secondary bg-white border border-secondary shadow-sm" style={{ fontSize: '0.85rem' }}>
                                        <FontAwesomeIcon icon={faWaveSquare} className="me-2" />
                                        Tendencias
                                    </span>
                                <div className="border-top flex-grow-1"></div>
                            </div>

                            {
                                chart.proyectos && chart.numeroArchivosProyectos &&
                                    chart.tiposInformacion && chart.numeroArchivosTiposInformacion &&
                                    (
                                        <div className="row g-4 mb-4">
                                            <div className="col-12 col-lg-6">
                                                <Proyectos labels={chart.proyectos} data={chart.numeroArchivosProyectos} />
                                            </div>
                                            <div className="col-12 col-lg-6">
                                                <TiposInformacion labels={chart.tiposInformacion} data={chart.numeroArchivosTiposInformacion} />
                                            </div>
                                        </div>
                                    )
                            }

                            {
                                chart.anios && chart.numeroArchivosAnios &&
                                    chart.especies && chart.numeroArchivosEspecies &&
                                    (
                                        <div className="row g-4 mb-4">
                                            <div className="col-12 col-lg-6">
                                                <Anios labels={chart.anios} data={chart.numeroArchivosAnios} />
                                            </div>
                                            <div className="col-12 col-lg-6">
                                                <Especies labels={chart.especies} data={chart.numeroArchivosEspecies} />
                                            </div>
                                        </div>
                                    )
                            }

                            <div className="d-flex align-items-center justify-content-center gap-3 text-secondary my-4">
                                <div className="border-top flex-grow-1"></div>
                                    <span className="badge rounded-pill py-2 px-4 fw-semibold text-secondary bg-white border border-secondary shadow-sm" style={{ fontSize: '0.85rem' }}>
                                        <FontAwesomeIcon icon={faWaveSquare} className="me-2" />
                                        Comparativas
                                    </span>
                                <div className="border-top flex-grow-1"></div>
                            </div>

                            {
                                chart.puertos && chart.numeroArchivosPuertos &&
                                    chart.regiones && chart.numeroArchivosRegiones &&
                                    (
                                        <div className="row g-4">
                                            <div className="col-12 col-xl-6">
                                                <Puertos labels={chart.puertos} data={chart.numeroArchivosPuertos} />
                                            </div>
                                            <div className="col-12 col-xl-6">
                                                <Regiones col={'col-12'} labels={chart.regiones} data={chart.numeroArchivosRegiones} />
                                            </div>
                                        </div>
                                    )
                            }

                        </>
                    ) :
                    (
                        <div className="d-flex justify-content-center align-items-center">
                            <Spinner />
                        </div>
                    )
                }

            </div>
        );
    }

    return (
        <Sidebav Dashboard={Dashboard} />
    );
}

export default DashboardPage;
